// src/contexts/AuthContext.jsx
import { createContext, useState, useContext, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import i18n from '../i18n/config';
import { createAuthUtils } from '../utils/authUtils';
import { createLogger } from '@/utils/logger.js';

const logger = createLogger('Shell::Authentication');

// Cookie configuration
const AUTH_TOKEN_COOKIE = 'auth_token';
const COOKIE_OPTIONS = {
    secure: true,
    sameSite: 'strict',
    expires: 7
};

// Initial permissions state
const INITIAL_PERMISSIONS = {
    roles: [],
    features: [],
    capabilities: null,   // null = not yet loaded → auth.can() fails open (gateway still enforces)
    company: [],
    factories: [],
    ous: [],
    machines: []
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(Cookies.get(AUTH_TOKEN_COOKIE));
    const [permissions, setPermissions] = useState(INITIAL_PERMISSIONS);
    const [loading, setLoading] = useState(true);

    // Create auth utilities for MFEs
    const authUtils = useMemo(() => {
        if (!user) return null;
        return createAuthUtils({ user, permissions });
    }, [user, permissions]);

    // ========================================================================
    // User Type Helpers
    // ========================================================================

    const isSuperuser = useMemo(() => {
        return permissions.roles?.includes('superuser') ?? false;
    }, [permissions.roles]);

    const hasAssignedCompany = useMemo(() => {
        return (permissions.company?.length ?? 0) > 0;
    }, [permissions.company]);

    const primaryCompany = useMemo(() => {
        return permissions.company?.[0] ?? null;
    }, [permissions.company]);

    // ========================================================================
    // Initialization
    // ========================================================================

    useEffect(() => {
        const initializeAuth = async () => {
            const sessionCookie = Cookies.get(AUTH_TOKEN_COOKIE);

            if (!sessionCookie) {
                logger.info('No session cookie found');
                resetAuthState();
                setLoading(false);
                return;
            }

            logger.info('Session cookie found, fetching profile...');
            await fetchProfile();
            setLoading(false);
        };

        initializeAuth();
    }, []);

    // ========================================================================
    // Auth State Management
    // ========================================================================

    const resetAuthState = () => {
        setUser(null);
        setToken(null);
        setPermissions(INITIAL_PERMISSIONS);
    };

    /**
     * Clear all client-side auth artifacts.
     * Runs regardless of whether the backend logout succeeded.
     */
    const clearLocalSession = () => {
        // Notify other contexts (SSE, cache, etc.) before clearing state
        window.dispatchEvent(new CustomEvent('shell:logout'));

        resetAuthState();
        Cookies.remove(AUTH_TOKEN_COOKIE);

        // Clear sessionStorage (SSE client ID)
        sessionStorage.removeItem('sse_client_id');

        // Clear shell-namespaced localStorage (keep theme preference)
        Object.keys(localStorage)
            .filter(key => key.startsWith('shell:') && key !== 'shell:theme')
            .forEach(key => localStorage.removeItem(key));

        i18n.changeLanguage(navigator.language.split('-')[0]);
    };

    const login = async (credentials) => {
        try {
            const loginUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_AUTH_ENDPOINT}`;
            logger.info('Attempting login...');

            const response = await fetch(loginUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });

            if (!response.ok) {
                logger.error('Login failed with status:', response.status);
                return false;
            }

            const data = await response.json();

            Cookies.set(AUTH_TOKEN_COOKIE, data.token, COOKIE_OPTIONS);
            logger.info('Session cookie set, fetching user profile');

            return await fetchProfile();
        } catch (error) {
            logger.error('Login error:', error);
            return false;
        }
    };

    const logout = () => {
        logger.info('Logging out...');

        const currentToken = Cookies.get(AUTH_TOKEN_COOKIE);

        // Fire-and-forget: invalidate server-side session without blocking UI
        if (currentToken) {
            const logoutUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_LOGOUT_ENDPOINT}`;

            fetch(logoutUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                }
            }).catch(error => {
                logger.warn('Backend logout request failed:', error.message);
            });
        }

        clearLocalSession();
    };

    const fetchProfile = async () => {
        try {
            const profileUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_PROFILE_ENDPOINT}`;
            const currentToken = Cookies.get(AUTH_TOKEN_COOKIE);

            if (!currentToken) {
                logger.warn('No session token found');
                clearLocalSession();
                return false;
            }

            const response = await fetch(profileUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logger.warn('Session expired or invalid');
                    clearLocalSession();
                    return false;
                }
                throw new Error('Failed to fetch profile');
            }

            const data = await response.json();

            const userData = {
                firstName: data.first_name,
                lastName: data.last_name,
                language: data.language
            };

            const userPermissions = {
                roles: data.permissions?.roles || [],
                features: data.permissions?.services ?? data.permissions?.features ?? [],
                // features: [
                //     "dashboard",
                //     "edit_mode",
                //     "flexible_shifts",
                //     "shopfloor_ai",
                //     "notifications",
                //     "user_management"],
                company: data.permissions?.companies || [],
                factories: data.permissions?.factories || [],
                ous: data.permissions?.ous || [],
                machines: data.permissions?.machines || [],
                // input_codes the user may call — TOP-LEVEL on the profile response (gateway
                // ProfileHandler), not under permissions. null when an older gateway omits it
                // → auth.can() fails open (gateway still enforces).
                capabilities: data.capabilities ?? null
            };

            setUser(userData);
            setPermissions(userPermissions);
            setToken(currentToken);

            if (userData.language) {
                await i18n.changeLanguage(userData.language);
                logger.info('Language set to:', userData.language);
            }

            logger.info('Profile loaded', {
                roles: userPermissions.roles,
                features: userPermissions.features,
                companyCount: userPermissions.company.length,
                isSuperuser: userPermissions.roles.includes('superuser')
            });

            return true;
        } catch (error) {
            logger.error('Profile fetch error:', error);
            clearLocalSession();
            return false;
        }
    };

    // ========================================================================
    // Permission Checking
    // ========================================================================

    const hasFeature = (featureName) => {
        return permissions.features?.includes(featureName) || false;
    };

    // Legacy methods for backward compatibility with MFEs
    const hasPermission = (permission) => authUtils?.hasPermission(permission) || false;
    const hasAnyPermission = (roleNames) => authUtils?.hasAnyPermission(roleNames) || false;
    const isInGroup = (groupName) => authUtils?.isInGroup(groupName) || false;

    // ========================================================================
    // Context Value
    // ========================================================================

    const contextValue = {
        // Auth state
        user,
        token: Cookies.get(AUTH_TOKEN_COOKIE),
        loading,
        permissions,
        isAuthenticated: !!user,

        // Auth actions
        login,
        logout,

        // User type helpers
        isSuperuser,
        hasAssignedCompany,
        primaryCompany,

        // Feature checking (preferred)
        hasFeature,

        // Legacy permission checking (for MFE backward compatibility)
        hasPermission,
        hasAnyPermission,
        isInGroup,

        // Auth utilities for MFEs
        authUtils
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;